import React, { useEffect, useState } from "react";
import {
  Upload,
  Button,
  Form,
  Typography,
  Row,
  Col,
  message,
  Card,
} from "antd";
import { InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const { Dragger } = Upload;
const { Title } = Typography;

const allowedTypes = [".jpg", ".png", ".jpeg", ".pdf"];
const maxSizeMB = 10;

const MyUpload = ({
  shouldFetch = true,
  deleteEndpoint,
  fileFetchEndpoint,
  fileResponseKey,
  normalizedKeys,
  fileFetchStateKey = "id",
  form,
  formItems,
  title,
  description,
  colSpan = 12,
  onUpload,
  maxCount = 4,
  mimeTypes = ["image/jpeg", "image/png", "application/pdf"],
}) => {
  const [loading, setLoading] = useState(false);
  const locationState = useLocation();
  const [fileList, setFileList] = useState([]);
  const navigate = useNavigate();

  const handleBeforeUpload = (file) => {
    const isAllowed = allowedTypes.some((type) => file.name.endsWith(type));
    const isSizeOk = file.size / 1024 / 1024 < maxSizeMB;

    if (!isAllowed) {
      message.error(`${file.name} is not an allowed file type`);
      return Upload.LIST_IGNORE;
    }
    if (!isSizeOk) {
      message.error(`${file.name} exceeds the 10MB limit`);
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  useEffect(() => {
    fetchRelatedFiles();
  }, []);

  async function fetchRelatedFiles() {
    if (shouldFetch) {
      const res = await Utilities.fetchWithAuth(
        "GET",
        `${fileFetchEndpoint}/${locationState?.state?.[fileFetchStateKey]}`,
        undefined,
        navigate,
      );
      if (res?.ok) {
        const files = res?.data?.[fileResponseKey]?.map((v) => ({
          ...v,
          fromDB: true,
        }));
        setFileList(files);
      }
    }
  }

  async function deleteDBFile(actions, file) {
    const r = await removeFileFromDB(file);
    if (r) actions.remove();
  }

  async function deleteFile(actions, file) {
    actions.remove();
  }

  async function removeFileFromDB(file) {
    console.log(file);
    //remove file based on ID and not admissionID
    // console.log(file);return;
    if (!file.fromDB) return;
    const res = await Utilities.fetchWithAuth(
      "DELETE",
      `${deleteEndpoint}/${file?.[normalizedKeys?.id]}`,
      { id: file?.[normalizedKeys?.id] },
      navigate,
    );
    if (res?.ok) {
      Utilities.showNotification(undefined, res?.msg, "text-success");
      return true;
    } else {
      Utilities.showNotification(undefined, res?.msg);
      return false;
    }
  }

  function itemToRender(originNode, file, fileList, actions) {
    if (file?.fromDB) {
      return (
        <div className="row align-items-center py-2 border-bottom gx-3">
          <div className="col">
            <a
              href={`${Settings.backend}/${file?.[normalizedKeys?.url]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary text-break"
            >
              {file?.[normalizedKeys?.name]}
            </a>
          </div>
          <div className="col-auto">
            <Button
              icon={<DeleteOutlined />}
              onClick={(e) => deleteDBFile(actions, file)}
              type="link"
              danger
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className="row align-items-center py-2 border-bottom gx-3">
          <div className="col">
            <label className="text-primary text-break">{file?.name}</label>
          </div>
          <div className="col-auto">
            <Button
              icon={<DeleteOutlined />}
              onClick={(e) => deleteFile(actions, file)}
              type="link"
              danger
            />
          </div>
        </div>
      );
    }
  }

  async function validate(_, val) {
    const allValues = form?.getFieldsValue();
    const value = allValues?.uploads || [];
    // MIME type validation
    for (const file of value) {
      const mime = file.type || file.originFileObj?.type;
      if (!mimeTypes.includes(mime)) {
        throw new Error(
          `Invalid file type(s).  Allowed: ${mimeTypes.join(", ")}`,
        );
      }
    }
    return Promise.resolve();
  }

  function uploadJSX() {
    return (
      <Form.Item
        name="uploads"
        rules={[
          {
            validator: validate,
          },
        ]}
      >
        <Dragger
          maxCount={maxCount}
          fileList={fileList}
          onChange={({ fileList }) => {
            typeof setFileList == "function" && setFileList(fileList);
            form.setFieldsValue({ uploads: fileList }); // sync with Form
          }}
          itemRender={itemToRender}
          accept=".jpg,.jpeg,.png,.pdf"
          beforeUpload={handleBeforeUpload}
          multiple
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag files to this area to upload
            <br />
            <label className="text-danger">
              Files to Upload: {description}
            </label>
          </p>
          <p className="ant-upload-hint">
            Supported formats: .jpg, .jpeg, .png, .pdf — Max size: 10MB
          </p>
        </Dragger>
      </Form.Item>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={colSpan}>
        <Card>
          <Title level={3}>{title}</Title>
          {formItems?.(
            uploadJSX,
            onUpload,
            { form, locationState, navigate },
            loading,
            setLoading,
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default MyUpload;
